import dayjs from 'dayjs';
import { FLOWS, percentuale } from '@leopart/calc';
import { RESPONSIBLE_OPTIONS, TbResponsible, TrainingMixGranularity } from '../../../models/allenamenti';
import { DistributionSlice } from '../../../shared/charts/chart.types';

export type AnalysisPeriod = 'week' | 'month' | 'season';

/** Le tre viste di aggregazione della vista Analisi. */
export const PERIOD_OPTIONS: { value: AnalysisPeriod; label: string }[] = [
  { value: 'week', label: 'Settimana' },
  { value: 'month', label: 'Mese' },
  { value: 'season', label: 'Stagione' },
];

/**
 * "2h 5m" / "45m" — stessa convenzione già in uso in
 * TrainingStatisticsDashboard (fmtMinutes): non introduce una quarta forma
 * di "minuti totali" nel repo (R8, design M2/M3).
 */
export function formatMinutes(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * null → "—", mai "0%": la percentuale arriva già a 1 decimale da
 * @leopart/calc (`percentuale()`), qui si formatta soltanto.
 */
export function formatPercent(percent: number | null): string {
  return percent == null ? '—' : `${percent.toFixed(1)}%`;
}

export function formatLocalDateIt(iso: string): string {
  return dayjs(iso).format('DD/MM/YYYY');
}

/** Bucket della serie temporale → etichetta asse, dipende dalla granularità del periodo. */
export function formatBucket(bucket: string, granularity: TrainingMixGranularity): string {
  if (granularity === 'month') return dayjs(`${bucket}-01`).format('MMM YY');
  return dayjs(bucket).format('DD/MM');
}

export function responsibleLabel(value: TbResponsible): string {
  return RESPONSIBLE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/**
 * Fette FLOWS→DistributionSlice per un set di minuti per modalità (continuo/
 * intermittente/analitico): unica sorgente per label/colore/percentuale,
 * riusata da tutti i pannelli col breakdown per modalità (Dimensione dei
 * gruppi, Densità di gioco) — niente soglie o colori ricalcolati altrove.
 *
 * `includeFactor` aggiunge il suffisso `(×0.65)` all'etichetta: va disattivato
 * nelle mini-card strette (colonna 2×2), dove l'etichetta lunga andrebbe a
 * capo — il fattore resta visibile nel blocco "Modalità complessiva" a piena
 * larghezza.
 */
export function buildFlowBars(
  byFlow: { flow: string; minutes: number }[],
  includeFactor = true,
): DistributionSlice[] {
  const total = byFlow.reduce((s, f) => s + f.minutes, 0);
  return FLOWS.map((flow) => {
    const minutes = byFlow.find((f) => f.flow === flow.id)?.minutes ?? 0;
    return {
      id: flow.id,
      label: includeFactor ? `${flow.labelIt} (×${flow.fattore.toFixed(2)})` : flow.labelIt,
      value: minutes,
      percent: percentuale(minutes, total),
      color: flow.color,
    };
  });
}
